import React, { useMemo, useEffect, useRef } from "react";
import useSWRInfinite from "swr/infinite";

type InfiniteFetcherKey =
  | Record<string, unknown>
  | string
  | null
  | false
  | undefined;
type InfiniteFetcherParams = Record<string, unknown> & {
  limit?: number;
  offset?: number;
  page?: number;
};
type InfiniteFetcherFn<TData> = (
  _params: InfiniteFetcherParams,
  _shouldFetch: boolean
) => Promise<TData>;
type InfiniteFetcherOptions = Record<string, unknown> & {
  cacheKeyPrefix?: string;
};

function useInfiniteFetcher<TData = unknown>(
  key: InfiniteFetcherKey,
  fetcherFn: InfiniteFetcherFn<TData>,
  options: InfiniteFetcherOptions = {}
) {
  const { cacheKeyPrefix, ...swrOptions } = options;
  const serializedParams = key
    ? typeof key === "object"
      ? JSON.stringify(key)
      : key
    : null;
  const serializedKey =
    serializedParams && cacheKeyPrefix
      ? `${cacheKeyPrefix}:${serializedParams}`
      : serializedParams;

  const keyRef = React.useRef(key);
  keyRef.current = key;

  const prevKeyRef = useRef(serializedKey);
  const getKey = useMemo(
    () => (pageIndex: number) => {
      if (!serializedKey) return null;
      return [serializedKey, pageIndex];
    },
    [serializedKey]
  );

  const fetcher = useMemo(() => {
    return ([_serializedKey, page]: [string, number]) => {
      const keyData = keyRef.current;
      const params: InfiniteFetcherParams =
        keyData && typeof keyData === "object" ? { ...keyData } : {};
      params.page = page + 1;
      if (typeof params.limit === "number") {
        params.offset = page * params.limit;
      }
      return fetcherFn(params, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcherFn, serializedKey]);

  const { data, error, isLoading, isValidating, mutate, size, setSize } =
    useSWRInfinite(getKey, fetcher, {
      revalidateOnMount: true,
      revalidateAll: false,
      revalidateFirstPage: false,
      ...swrOptions,
      shouldRetryOnError: false,
      onErrorRetry: () => {},
    });

  useEffect(() => {
    if (prevKeyRef.current !== serializedKey) {
      prevKeyRef.current = serializedKey;
      if (size > 1) setSize(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey]);

  return {
    data,
    error,
    isLoading: isLoading && !data,
    isValidating,
    mutate,
    size,
    setSize,
  };
}

export default useInfiniteFetcher;
